import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { APP_INITIALIZER, Injectable, NgModule, TemplateRef } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { environment as env } from 'environments/environment';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular-26';
import { Observable, firstValueFrom, switchMap, throwError, timeout } from 'rxjs';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { ComponentType } from '@angular/cdk/portal';
import { PlatformLocation } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogConfig, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import _ from 'lodash';
import { ErrorDialog } from './c/error-dialog/error-dialog';
import { ThemeMenuComponent } from './c/theme-menu/theme-menu.component';

@Injectable()
export class CustomInterceptor implements HttpInterceptor {

  constructor() { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.url.includes('/graphql') && !request.headers.has('Content-Type')) {
      request = request.clone({
        setHeaders: {
          'Accept': 'application/json',
          'Content-Type': 'application/graphql'
        }
      });
    } else if (request.url.includes('/auth')) {
      request = request.clone({
        setHeaders: {
          'Access-Control-Allow-Origin': '*'
        }
      });
    }
    return next.handle(request);
  }
}

export function initializer(
  loc: PlatformLocation,
  http: HttpClient,
  keycloak: KeycloakService
): () => Promise<any> {
  return () => getConfigVariables(loc, http).
    then(() => initKeycloak(keycloak));
}

function initKeycloak(keycloak: KeycloakService): Promise<any> {
  return keycloak.init({
    config: {
      url: env.auth.urlPath,
      realm: env.auth.realm,
      clientId: env.auth.clientID,
    },
    initOptions: {
      onLoad: 'login-required',
      checkLoginIframe: false,
      //scope: 'recordings'
    },
    enableBearerInterceptor: true,
    // bearerExcludedUrls: ['/assets', '/clients/public']
  });
}

function getConfigVariables(
  loc: PlatformLocation,
  http: HttpClient
): Promise<void> {
  if (!env.production) {
    return Promise.resolve();
  }
  return firstValueFrom(
    http.get(
      // (loc.getBaseHrefFromDOM() + '/assets/configuration-variables.json?_='+Date.now()).replaceAll('//', '/')
      (loc.getBaseHrefFromDOM() + '/assets/configuration-variables.json?_=' + Date.now()).replace('//', '/')
    ).pipe(
      timeout(1000),
      switchMap(config => {
        _.assignIn(env, config);
        const emptyFields = env.getEmptyFields();
        if (!_.isEmpty(emptyFields)) {
          return throwError(() => 'Configuration variable fields are not set: '
            + `${emptyFields.map(v => `'${v}'`).join(', ')}`);
        }
        return Promise.resolve();
      }),
    )
  );
}

const origMatDialogOpenFn = MatDialog.prototype.open;
MatDialog.prototype.open = function <T, D = any, R = any>(
  template: ComponentType<T> | TemplateRef<T>,
  config?: MatDialogConfig<D>,
): MatDialogRef<T, R> {
  const buttonElement = document.activeElement as HTMLElement; // Get the currently focused element
  buttonElement.blur(); // Remove focus from the button
  return origMatDialogOpenFn.bind(this)(template, config);
};

@NgModule({
  declarations: [
    AppComponent,
    ThemeMenuComponent,
    ErrorDialog,
    //MathJaxSpanComponent,
  ],
  imports: [
    AppRoutingModule,
    BrowserModule,
    HttpClientModule,
    BrowserAnimationsModule,
    MatMenuModule,
    MatToolbarModule,
    MatListModule,
    MatSidenavModule,
    MatButtonModule,
    MatIconModule,
    KeycloakAngularModule,
    MatDividerModule,
    MatTooltipModule,
    MatDialogModule,
  ],
  providers: [
    { // ENVIRONMENT_INITIALIZER
      provide: APP_INITIALIZER,
      useFactory: initializer,
      multi: true,
      deps: [
        PlatformLocation,
        HttpClient,
        KeycloakService,
      ]
    },
    /*{
      provide: ErrorHandler,
      useClass: RavenErrorHandler
    }*/
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CustomInterceptor,
      multi: true
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
